import React, { useEffect, useState } from 'react';
import { View, StyleSheet, Image, StyleProp, ViewStyle, Pressable, Text, Platform } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Asset } from 'expo-asset';
import * as FileSystem from 'expo-file-system/legacy';
import Share from 'react-native-share';
import ThemedText from '../common/ThemedText';
import MentionText from '../common/MentionText';
import { useTheme } from '../../theme/ThemeContext';
import { getTimeAgo } from '../../utils/timeAgo';
import FeedVideoPlayer from './FeedVideoPlayer';

type IoniconName = React.ComponentProps<typeof Ionicons>['name'];

export interface FeedPost {
  id: string;
  userId: string;
  user: string;
  location: string;
  avatar?: string;
  image?: string;
  video?: string;
  caption?: string;
  likes: string;
  comments: string;
  isLiked?: boolean;
  createdAt?: string;
}

const engagementActions: { icon: IoniconName; key: 'likes' | 'comments'; tint?: string }[] = [
  { icon: 'trending-up-outline', key: 'likes' },
  { icon: 'chatbubble-ellipses-outline', key: 'comments' },
];

interface FeaturedFeedPostProps {
  post: FeedPost;
  style?: StyleProp<ViewStyle>;
  currentUserId?: string;
  isAdmin?: boolean;
  onLike?: () => void;
  onDelete?: () => void;
  onEdit?: () => void;
  onUserPress?: (userId: string) => void;
  onCommentPress?: () => void;
}

const FeaturedFeedPost: React.FC<FeaturedFeedPostProps> = ({ post, style, currentUserId, isAdmin = false, onLike, onDelete, onEdit, onUserPress, onCommentPress }) => {
  const { theme } = useTheme();
  const [showFullCaption, setShowFullCaption] = useState(false);
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    console.log(`[FeaturedFeedPost] mounted, post=${post.id}`);
  }, [post.id]);

  const handleExpandCaption = () => {
    console.log(`[FeaturedFeedPost] caption expanded for post=${post.id}`);
    setShowFullCaption(true);
  };

  const handleLikePress = () => {
    if (onLike) {
      onLike();
    }
  };

  const handleMenuPress = () => {
    setShowMenu(!showMenu);
  };

  const handleEdit = () => {
    setShowMenu(false);
    if (onEdit) onEdit();
  };

  const handleDelete = () => {
    setShowMenu(false);
    if (onDelete) onDelete();
  };

  const handleShare = async () => {
    try {
      console.log('🔵 [FeaturedFeedPost] Starting share...');
      console.log('🔵 Post ID:', post.id);
      console.log('🔵 Post has image:', !!post.image);
      console.log('🔵 Image URL:', post.image);
      console.log('🔵 Platform:', Platform.OS);
      
      const postUrl = `https://lnbapp.ln.ink/post/${post.id}`;
      const shareMessage = `Check out this post on Leads n Business 🚀\n\n${post.caption || ''}\n\n${postUrl}`;
      
      // Determine image source: post image or default
      let imageSource = post.image;
      let useDefaultImage = false;
      
      if (!imageSource && Platform.OS !== 'web') {
        // No post image, use default post.png
        console.log('🔵 No post image, using default post.png');
        useDefaultImage = true;
        
        try {
          const asset = Asset.fromModule(require('../../../assets/post.png'));
          await asset.downloadAsync();
          imageSource = asset.localUri || asset.uri;
          console.log('🔵 Default image URI:', imageSource);
        } catch (assetError) {
          console.error('❌ Error loading default image:', assetError);
          imageSource = undefined;
        }
      }
      
      // If we have an image source (either post image or default), download and share it
      if (imageSource && Platform.OS !== 'web') {
        console.log('🔵 Attempting to prepare image for sharing...');
        console.log('🔵 Image source:', imageSource);
        
        try {
          let fileUriToShare: string;
          
          // If using default image (local asset), copy it to cache first
          if (useDefaultImage) {
            console.log('🔵 Copying local default image to cache');
            const fileUri = `${FileSystem.cacheDirectory}default_post.png`;
            
            // Copy the asset to a file location that can be shared
            await FileSystem.copyAsync({
              from: imageSource,
              to: fileUri,
            });
            
            console.log('🔵 Default image copied to:', fileUri);
            fileUriToShare = fileUri;
          } else {
            // For remote images, download them first
            const fileUri = `${FileSystem.cacheDirectory}post_${post.id}.jpg`;
            console.log('🔵 Downloading remote image to:', fileUri);
            
            const downloadResult = await FileSystem.downloadAsync(imageSource, fileUri);
            console.log('🔵 Download result status:', downloadResult.status);
            
            if (downloadResult.status !== 200) {
              console.log('❌ Download failed with status:', downloadResult.status);
              throw new Error('Download failed');
            }
            
            console.log('✅ Image downloaded successfully!');
            fileUriToShare = downloadResult.uri;
          }
          
          // Share image with message using react-native-share
          await Share.open({
            title: 'Share Post',
            message: shareMessage,
            url: Platform.OS === 'android' ? `file://${fileUriToShare}` : fileUriToShare,
            type: 'image/png',
          });
          console.log('✅ Post shared successfully with image');
          return;
        } catch (imageError: any) {
          if (imageError.message !== 'User did not share') {
            console.error('❌ Error preparing/sharing post image:', imageError);
          }
        }
      } else {
        console.log('⚠️ Skipping image download:', {
          hasImage: !!imageSource,
          isWeb: Platform.OS === 'web'
        });
      }
      
      // Fallback: Share just the message with URL
      console.log('🔵 Falling back to message-only share');
      await Share.open({
        message: shareMessage,
      });
      console.log('✅ Share completed (message only)');
    } catch (error: any) {
      if (error.message !== 'User did not share') {
        console.error('❌ Error sharing post:', error);
      }
    }
  };

  const isOwner = currentUserId && currentUserId === post.userId;
  const canModify = isAdmin || isOwner;

  return (
    <View style={[styles.container, style]}>
      <View style={styles.postHeader}>
        <Pressable 
          style={styles.profileMeta}
          onPress={() => {
            console.log('[FeaturedFeedPost] User pressed, userId:', post.userId);
            onUserPress?.(post.userId);
          }}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          {post.avatar ? (
            <Image source={{ uri: post.avatar }} style={styles.avatar} />
          ) : (
            <View style={[styles.avatar, { backgroundColor: theme.backgroundSecondary, justifyContent: 'center', alignItems: 'center' }]}>
              <Ionicons name="person" size={24} color={theme.textSecondary} />
            </View>
          )}
          <View style={styles.profileText}>
            <ThemedText variant="semibold" style={styles.username}>
              {post.user}
            </ThemedText>
            <ThemedText style={[styles.metaText, { color: theme.textSecondary }]}>
              {post.location}{post.createdAt ? ` • ${getTimeAgo(post.createdAt)}` : ''}
            </ThemedText>
          </View>
        </Pressable>
        <Pressable onPress={handleMenuPress}>
          <Ionicons name="ellipsis-horizontal" size={22} color={theme.textSecondary} />
        </Pressable>
      </View>

      {/* Menu Dropdown */}
      {showMenu && canModify && (
        <View style={[styles.menuContainer, { backgroundColor: theme.card, borderColor: theme.border }]}>
          {isOwner && (
            <Pressable onPress={handleEdit} style={styles.menuItem}>
              <Ionicons name="create-outline" size={20} color={theme.text} />
              <ThemedText style={styles.menuText}>Edit</ThemedText>
            </Pressable>
          )}
          <Pressable onPress={handleDelete} style={styles.menuItem}>
            <Ionicons name="trash-outline" size={20} color={theme.error} />
            <ThemedText style={[styles.menuText, { color: theme.error }]}>Delete</ThemedText>
          </Pressable>
        </View>
      )}

      {post.caption && (
        <>
          <MentionText
            text={post.caption}
            style={[styles.captionText, { color: theme.text }] as any}
            numberOfLines={showFullCaption ? undefined : 3}
          />
          {!showFullCaption && post.caption.length > 100 && (
            <Pressable onPress={handleExpandCaption} style={styles.seeMoreButton}>
              <ThemedText variant="semibold" style={[styles.seeMoreText, { color: theme.info }]}>
                See more
              </ThemedText>
            </Pressable>
          )}
        </>
      )}

      {post.video ? (
        <FeedVideoPlayer uri={post.video} style={styles.postImage} />
      ) : post.image ? (
        <Image source={{ uri: post.image }} style={styles.postImage} resizeMode="contain" />
      ) : null}

      <View style={styles.actionsRow}>
        {engagementActions.map((action) => (
          <Pressable 
            key={action.key} 
            style={styles.actionItem}
            onPress={action.key === 'likes' ? handleLikePress : action.key === 'comments' ? onCommentPress : undefined}
          >
            {action.key === 'likes' ? (
              <Ionicons 
                name={post.isLiked ? 'trending-up' : 'trending-up-outline'}
                size={24} 
                color={post.isLiked ? theme.primary : theme.text} 
              />
            ) : (
              <Ionicons 
                name={action.icon} 
                size={24} 
                color={theme.text} 
              />
            )}
            <ThemedText style={[styles.actionText, { color: theme.text }]}>
              {post[action.key]}
            </ThemedText>
          </Pressable>
        ))}
        <View style={styles.actionsSpacer} />
        <Pressable onPress={handleShare} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
          <Ionicons name="paper-plane-outline" size={24} color={theme.text} />
        </Pressable>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%',
  },
  postHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  profileMeta: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  profileText: {
    marginLeft: 12,
  },
  avatar: {
    width: 46,
    height: 46,
    borderRadius: 23,
  },
  username: {
    fontSize: 17,
  },
  metaText: {
    fontSize: 13,
    aspectRatio: 12,
  },
  postImage: {
    width: '100%',
    aspectRatio: 1,
    borderRadius: 28,
    marginTop: 24,
  },
  captionText: {
    fontSize: 16,
    marginTop: 16,
  },
  seeMoreButton: {
    marginTop: 6,
    alignSelf: 'flex-start',
  },
  seeMoreText: {
    fontSize: 14,
  },
  actionsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 20,
  },
  actionItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginRight: 18,
  },
  actionText: {
    fontSize: 15,
    marginLeft: 6,
  },
  actionsSpacer: {
    flex: 1,
  },
  menuContainer: {
    position: 'absolute',
    top: 50,
    right: 16,
    borderRadius: 8,
    borderWidth: 1,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    zIndex: 1000,
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 16,
    gap: 12,
  },
  menuText: {
    fontSize: 15,
  },
  menuDivider: {
    height: 1,
  },
});

export default FeaturedFeedPost;
